<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-12">
                <div class="card mb-4">
                    <div class="card-header pb-0">
                        <div class="d-flex justify-content-between align-items-center">
                            <h6 class="mb-0">Reporte de nómina por producción</h6>
                            <router-link :to="{ name: 'Nomina' }" class="btn btn-outline-secondary btn-sm mb-0">
                                <i class="fas fa-arrow-left me-1"></i>
                                Volver
                            </router-link>
                        </div>

                        <!-- Filtros -->
                        <div class="row g-2 align-items-end mt-2">
                            <div class="col-md-3 mb-3">
                                <label class="mb-1">Producción</label>
                                <select v-model="filters.production_id" class="form-select" @change="onProductionChange">
                                    <option value="">Seleccionar producción</option>
                                    <option v-for="p in options.productions" :key="p.id" :value="p.id">{{ p.name }}</option>
                                </select>
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="mb-1">Oficio</label>
                                <select v-model="filters.oficio_id" class="form-select" :disabled="!filters.production_id" @change="fetchReport">
                                    <option value="">Todos</option>
                                    <option v-for="o in options.oficios" :key="o.id" :value="o.id">{{ o.name }}</option>
                                </select>
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="mb-1">Canasta</label>
                                <select v-model="filters.basket_id" class="form-select" :disabled="!filters.production_id" @change="fetchReport">
                                    <option value="">Todas</option>
                                    <option v-for="b in options.baskets" :key="b.id" :value="b.id">{{ b.name }}</option>
                                </select>
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="mb-1">Presupuesto</label>
                                <input type="text" class="form-control" placeholder="Ej: 4.000.000"
                                    :value="budget ? formatPrice(budget) : ''" @input="onBudgetInput($event.target.value)" />
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="!filters.production_id" class="card">
                    <div class="card-body text-center text-sm text-secondary">
                        Seleccione una producción para ver el reporte.
                    </div>
                </div>

                <template v-else-if="report">
                    <!-- Comparación con el presupuesto -->
                    <div class="row">
                        <div class="col-md-3 mb-4" v-for="card in summaryCards" :key="card.label">
                            <div class="card h-100">
                                <div class="card-body p-3">
                                    <p class="text-sm mb-1 text-uppercase font-weight-bold text-secondary">{{ card.label }}</p>
                                    <h5 class="font-weight-bolder mb-0" :class="card.class">{{ card.value }}</h5>
                                    <p v-if="card.hint" class="text-xs mb-0 mt-1 text-secondary">{{ card.hint }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="report.summary.not_validated > 0 || report.summary.with_issues > 0" class="alert alert-warning text-white text-sm">
                        <span v-if="report.summary.not_validated > 0">
                            {{ report.summary.not_validated }} tarea(s) sin validar: su total puede cambiar con los días asistidos.
                        </span>
                        <span v-if="report.summary.with_issues > 0">
                            {{ report.summary.with_issues }} tarea(s) con observaciones (ver detalle abajo).
                        </span>
                    </div>

                    <!-- Por oficio y por operario -->
                    <div class="row">
                        <div class="col-lg-6 mb-4" v-for="section in sections" :key="section.title">
                            <div class="card h-100">
                                <div class="card-header pb-0"><h6>{{ section.title }}</h6></div>
                                <div class="card-body px-0 pt-0 pb-2">
                                    <div class="table-responsive p-0">
                                        <table class="table align-items-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">{{ section.label }}</th>
                                                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Tareas</th>
                                                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Días asist.</th>
                                                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Días faltó</th>
                                                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Calculado</th>
                                                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pagado</th>
                                                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pendiente</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="row in section.rows" :key="row.name">
                                                    <td class="text-sm"><h6 class="mb-0 text-sm px-2">{{ row.name }}</h6></td>
                                                    <td class="text-center text-sm">{{ row.tasks }}</td>
                                                    <td class="text-center text-sm">{{ row.attended_days }}</td>
                                                    <td class="text-center text-sm" :class="{ 'text-danger': row.absent_days > 0 }">{{ row.absent_days }}</td>
                                                    <td class="text-center text-sm">{{ formatMoney(row.calculated) }}</td>
                                                    <td class="text-center text-sm">{{ formatMoney(row.paid) }}</td>
                                                    <td class="text-center text-sm">{{ formatMoney(row.pending) }}</td>
                                                </tr>
                                                <tr v-if="section.rows.length === 0">
                                                    <td colspan="7" class="text-center text-xxs">No hay registros</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Detalle de tareas -->
                    <div class="card">
                        <div class="card-header pb-0 d-flex justify-content-between align-items-center">
                            <h6 class="mb-0">Detalle de tareas</h6>
                            <div class="form-check mb-0">
                                <input class="form-check-input" type="checkbox" id="onlyIssues" v-model="onlyIssues" />
                                <label class="form-check-label text-sm" for="onlyIssues">Solo con observaciones</label>
                            </div>
                        </div>
                        <div class="card-body px-0 pt-0 pb-2">
                            <div class="table-responsive p-0">
                                <table class="table align-items-center mb-0">
                                    <thead>
                                        <tr>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Operario</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Oficio</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Modelo</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"># Canasta</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cant</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Días asist.</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Días faltó</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Días transc.</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Valor</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Valor modelo</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pagado</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">F. Asignación</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">F. Entrega</th>
                                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">F. Validación</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Observaciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="task in visibleTasks" :key="task.id">
                                            <td class="text-sm">
                                                <h6 class="mb-0 text-sm px-2">{{ task.user || '-' }}</h6>
                                                <span v-if="task.group" class="badge badge-sm bg-gradient-info ms-2">
                                                    <i class="fa fa-user-friends me-1"></i>{{ task.group }}
                                                </span>
                                            </td>
                                            <td class="text-center text-sm">{{ task.oficio || '-' }}</td>
                                            <td class="text-center text-sm">{{ task.model || '-' }}</td>
                                            <td class="text-center text-sm">{{ task.basket ? '#' + task.basket : '-' }}</td>
                                            <td class="text-center text-sm">{{ task.quantity }}</td>
                                            <td class="text-center text-sm">{{ task.attended_days ?? '-' }}</td>
                                            <td class="text-center text-sm" :class="{ 'text-danger': task.absent_days > 0 }">{{ task.absent_days }}</td>
                                            <td class="text-center text-sm">{{ task.elapsed_days ?? '-' }}</td>
                                            <td class="text-center text-sm">{{ formatMoney(task.price) }}</td>
                                            <td class="text-center text-sm">{{ task.model_price !== null ? formatMoney(task.model_price) : '-' }}</td>
                                            <td class="text-center text-sm">{{ formatMoney(task.total) }}</td>
                                            <td class="text-center text-sm">{{ formatMoney(task.total_paid) }}</td>
                                            <td class="text-center text-sm">{{ formatDate(task.assigned_at) }}</td>
                                            <td class="text-center text-sm">{{ formatDate(task.delivered_date) }}</td>
                                            <td class="text-center text-sm">{{ formatDate(task.validated_date) }}</td>
                                            <td class="text-xs">
                                                <div v-for="issue in task.issues" :key="issue" class="text-warning">
                                                    <i class="fa fa-exclamation-triangle me-1"></i>{{ issue }}
                                                </div>
                                                <span v-if="task.issues.length === 0" class="text-success"><i class="fa fa-check"></i></span>
                                            </td>
                                        </tr>
                                        <tr v-if="visibleTasks.length === 0">
                                            <td colspan="16" class="text-center text-xxs">No hay registros disponibles</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import productionUsersService from '@/services/productionUsers.service';

export default {
    name: 'NominaReport',
    data() {
        return {
            filters: { production_id: '', oficio_id: '', basket_id: '' },
            options: { productions: [], oficios: [], baskets: [] },
            budget: 0,
            report: null,
            onlyIssues: false,
        };
    },
    computed: {
        summaryCards() {
            const s = this.report.summary;
            const cards = [];
            if (this.budget > 0) {
                const diff = this.budget - s.calculated;
                cards.push({ label: 'Presupuesto', value: this.formatMoney(this.budget) });
                cards.push({
                    label: 'Diferencia presupuesto vs calculado',
                    value: this.formatMoney(diff),
                    class: Math.abs(diff) < 1 ? 'text-success' : (diff < 0 ? 'text-danger' : 'text-warning'),
                    hint: Math.abs(diff) < 1 ? 'Coincide con el presupuesto'
                        : (diff < 0 ? 'El calculado supera el presupuesto' : 'Queda por debajo del presupuesto'),
                });
            }
            cards.push({ label: 'Total calculado', value: this.formatMoney(s.calculated), hint: 'Días asistidos × valor de cada tarea' });
            cards.push({ label: 'Total pagado', value: this.formatMoney(s.paid) });
            cards.push({ label: 'Pendiente por pagar', value: this.formatMoney(s.pending), class: s.pending > 0 ? 'text-warning' : 'text-success' });
            cards.push({ label: 'Días asistidos', value: s.attended_days, hint: `${s.elapsed_days} días transcurridos en total` });
            cards.push({ label: 'Días que faltaron', value: s.absent_days, class: s.absent_days > 0 ? 'text-danger' : '' });
            cards.push({ label: 'Tareas / operarios', value: `${s.tasks} / ${s.users}`, hint: `${s.quantity} prendas` });
            return cards;
        },
        sections() {
            return [
                { title: 'Por oficio', label: 'Oficio', rows: this.report.by_oficio },
                { title: 'Por operario', label: 'Operario', rows: this.report.by_user },
            ];
        },
        visibleTasks() {
            return this.onlyIssues ? this.report.tasks.filter(t => t.issues.length > 0) : this.report.tasks;
        },
    },
    methods: {
        onProductionChange() {
            this.filters.oficio_id = '';
            this.filters.basket_id = '';
            this.fetchReport();
        },
        async fetchReport() {
            const params = {};
            Object.entries(this.filters).forEach(([key, val]) => {
                if (val !== '' && val !== null) params[key] = val;
            });
            try {
                const { data } = await productionUsersService.getReport(params);
                this.options = data.options;
                this.report = data.summary ? data : null;
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error?.response?.data?.message || 'No se pudo cargar el reporte',
                });
            }
        },
        onBudgetInput(value) {
            this.budget = parseFloat(String(value).replace(/\./g, '').replace(',', '.')) || 0;
        },
        formatPrice(value) {
            return Math.floor(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        },
        formatMoney(value) {
            return parseFloat(value || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate(date) {
            if (!date) return '-';
            const parsed = new Date(date);
            return isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('es-ES');
        },
    },
    mounted() {
        this.fetchReport();
    },
};
</script>
